/**
 * BulkOperationRepository.ts - RBAC Bulk Assignment Repository
 *
 * FERP Repository Pattern - Frontend data access layer for bulk role operations
 *
 * GitHub Issue: #338
 * Week: 2 (Frontend Integration)
 *
 * FERP Principles:
 * - Repository Pattern: All bulk operation data access through this layer
 * - Data-Driven: All values from GraphQL backend
 * - Immutable Audit: All operations tracked
 * - Multi-Tenant: Complete tenant isolation
 * - Type-Safe: Full TypeScript type definitions
 */

import { ApolloClient, gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Type Definitions (matching backend GraphQL schema)
// ============================================================================

/**
 * Bulk Operation
 *
 * Complete record of async bulk assignment processing
 */
export interface BulkOperation {
  batchId: string;
  tenantId: string;
  totalRows: number;
  successCount: number;
  failureCount: number;
  status: BatchStatus;
  startedAt: string;
  completedAt?: string;
  processedBy: string;
  results: RowResult[];
  errorSummary?: string;
  filename: string;
  isRollbackable: boolean;
}

/**
 * Row Result
 *
 * Individual row processing result
 */
export interface RowResult {
  rowNumber: number;
  userId: string;
  roleId: string;
  roleName: string;
  operation: Operation;
  status: RowStatus;
  errorMessage?: string;
  assignmentId?: string;
}

/**
 * Batch Status
 */
export enum BatchStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

/**
 * Row Status
 */
export enum RowStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

/**
 * Operation Type
 */
export enum Operation {
  ASSIGN = 'ASSIGN',
  REVOKE = 'REVOKE',
  UPDATE_EXPIRATION = 'UPDATE_EXPIRATION',
}

// ============================================================================
// Input Types (for mutations)
// ============================================================================

export interface InitiateBulkOperationInput {
  tenantId: string;
  csvContent: string;
  processingMode: ProcessingModeInput;
  filename: string;
}

export interface ProcessingModeInput {
  modeType: 'stop_on_error' | 'continue_on_error' | 'dry_run';
  errorThreshold?: number; // 0.0 - 1.0 (only for continue_on_error)
}

export interface RollbackBulkOperationInput {
  batchId: string;
  tenantId: string;
  reason: string;
}

// ============================================================================
// GraphQL Queries
// ============================================================================

const GET_BULK_OPERATION_STATUS_QUERY = gql`
  query GetBulkOperationStatus($batchId: String!, $tenantId: String!) {
    getBulkOperationStatus(batchId: $batchId, tenantId: $tenantId) {
      batchId
      tenantId
      totalRows
      successCount
      failureCount
      status
      startedAt
      completedAt
      processedBy
      results {
        rowNumber
        userId
        roleId
        roleName
        operation
        status
        errorMessage
        assignmentId
      }
      errorSummary
      filename
      isRollbackable
    }
  }
`;

const LIST_BULK_OPERATIONS_QUERY = gql`
  query ListBulkOperations($tenantId: String!, $limit: Int) {
    listBulkOperations(tenantId: $tenantId, limit: $limit) {
      batchId
      tenantId
      totalRows
      successCount
      failureCount
      status
      startedAt
      completedAt
      processedBy
      filename
      isRollbackable
    }
  }
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

const INITIATE_BULK_OPERATION_MUTATION = gql`
  mutation InitiateBulkOperation($input: InitiateBulkOperationInput!) {
    initiateBulkOperation(input: $input) {
      batchId
      tenantId
      status
      totalRows
      successCount
      failureCount
      startedAt
      filename
      results {
        rowNumber
        status
        errorMessage
      }
    }
  }
`;

const CANCEL_BULK_OPERATION_MUTATION = gql`
  mutation CancelBulkOperation($batchId: String!, $tenantId: String!) {
    cancelBulkOperation(batchId: $batchId, tenantId: $tenantId) {
      batchId
      status
    }
  }
`;

const ROLLBACK_BULK_OPERATION_MUTATION = gql`
  mutation RollbackBulkOperation($input: RollbackBulkOperationInput!) {
    rollbackBulkOperation(input: $input) {
      batchId
      status
      successCount
      failureCount
      completedAt
    }
  }
`;

// ============================================================================
// BulkOperationRepository Class
// ============================================================================

/**
 * BulkOperationRepository - Frontend bulk assignment data access layer
 *
 * Extends BaseRepository for:
 * - Safety validation
 * - Audit trail integration
 * - Error handling
 * - Retry logic
 */
export class BulkOperationRepository extends BaseRepository<BulkOperation> {
  private apolloClient: ApolloClient;

  constructor(
    client: ApolloClient,
    auditTrail?: any
  ) {
    super();
    this.apolloClient = client;
    if (auditTrail) {
      this.auditTrail = auditTrail;
    }
  }

  // ==========================================================================
  // Query Operations
  // ==========================================================================

  /**
   * Get bulk operation status by batch ID
   *
   * @param batchId - Batch ID (backend Snowflake ID)
   * @param tenantId - Tenant ID
   * @returns Bulk operation with complete results
   */
  async getBulkOperationStatus(
    batchId: string,
    tenantId: string
  ): Promise<BulkOperation | null> {
    await this.validateRepositorySafety({
      operation: 'GET_BULK_OPERATION_STATUS',
      domain: 'RBAC',
      tenantId,
      entityType: 'BulkOperation',
      criticalityLevel: 'LOW',
      metadata: { batchId }
    });

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query: GET_BULK_OPERATION_STATUS_QUERY,
        variables: { batchId, tenantId },
        context: {
          headers: { 'x-tenant': tenantId }
        },
        fetchPolicy: 'network-only' // Always fetch latest status
      });

      const operation = result.data?.getBulkOperationStatus;

      if (this.auditTrail && operation) {
        await this.auditTrail.logOperationSuccess({
          operation: 'GET_BULK_OPERATION_STATUS',
          tenantId,
          entityType: 'BulkOperation',
          entityId: batchId,
          metadata: {
            status: operation.status,
            totalRows: operation.totalRows,
            successCount: operation.successCount
          },
          timestamp: Date.now()
        });
      }

      return operation;
    } catch (error) {
      console.error('[BulkOperationRepository] Failed to get bulk operation status:', error);
      throw error;
    }
  }

  /**
   * List bulk operations for tenant
   *
   * @param tenantId - Tenant ID
   * @param limit - Maximum number of operations to return (default 10, max 100)
   * @returns Array of bulk operations (summary info only)
   */
  async listBulkOperations(
    tenantId: string,
    limit: number = 10
  ): Promise<BulkOperation[]> {
    await this.validateRepositorySafety({
      operation: 'LIST_BULK_OPERATIONS',
      domain: 'RBAC',
      tenantId,
      entityType: 'BulkOperation',
      criticalityLevel: 'LOW',
      metadata: { limit }
    });

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query: LIST_BULK_OPERATIONS_QUERY,
        variables: { tenantId, limit: Math.min(Math.max(limit, 1), 100) },
        context: {
          headers: { 'x-tenant': tenantId }
        },
        fetchPolicy: 'network-only'
      });

      return result.data?.listBulkOperations ?? null;
    } catch (error) {
      console.error('[BulkOperationRepository] Failed to list bulk operations:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Mutation Operations (Admin Only)
  // ==========================================================================

  /**
   * Initiate bulk operation (process CSV)
   *
   * Starts async batch processing of role assignments/revocations
   *
   * @param input - Bulk operation input (CSV content + processing mode)
   * @returns Created bulk operation with initial status
   */
  async initiateBulkOperation(
    input: InitiateBulkOperationInput
  ): Promise<BulkOperation> {
    await this.validateRepositorySafety({
      operation: 'INITIATE_BULK_OPERATION',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'BulkOperation',
      criticalityLevel: 'CRITICAL',
      metadata: {
        filename: input.filename,
        modeType: input.processingMode.modeType,
        csvSize: input.csvContent.length
      }
    });

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation: INITIATE_BULK_OPERATION_MUTATION,
        variables: { input },
        context: {
          headers: { 'x-tenant': input.tenantId }
        }
      });

      const operation = result.data?.initiateBulkOperation;

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'INITIATE_BULK_OPERATION',
          tenantId: input.tenantId,
          entityType: 'BulkOperation',
          entityId: operation.batchId,
          metadata: {
            filename: input.filename,
            totalRows: operation.totalRows,
            modeType: input.processingMode.modeType
          },
          timestamp: Date.now()
        });
      }

      return operation;
    } catch (error) {
      console.error('[BulkOperationRepository] Failed to initiate bulk operation:', error);
      throw error;
    }
  }

  /**
   * Cancel bulk operation
   *
   * Cancels processing (if still in PROCESSING state)
   *
   * @param batchId - Batch ID
   * @param tenantId - Tenant ID
   * @returns Updated operation with CANCELLED status
   */
  async cancelBulkOperation(
    batchId: string,
    tenantId: string
  ): Promise<BulkOperation> {
    await this.validateRepositorySafety({
      operation: 'CANCEL_BULK_OPERATION',
      domain: 'RBAC',
      tenantId,
      entityType: 'BulkOperation',
      criticalityLevel: 'HIGH',
      metadata: { batchId }
    });

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation: CANCEL_BULK_OPERATION_MUTATION,
        variables: { batchId, tenantId },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      const operation = result.data?.cancelBulkOperation;

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'CANCEL_BULK_OPERATION',
          tenantId,
          entityType: 'BulkOperation',
          entityId: batchId,
          metadata: { status: operation.status },
          timestamp: Date.now()
        });
      }

      return operation;
    } catch (error) {
      console.error('[BulkOperationRepository] Failed to cancel bulk operation:', error);
      throw error;
    }
  }

  /**
   * Rollback bulk operation
   *
   * Revokes all successful assignments from a batch (within 24-hour window)
   *
   * @param input - Rollback input (batchId, tenantId, reason)
   * @returns Updated operation after rollback
   */
  async rollbackBulkOperation(
    input: RollbackBulkOperationInput
  ): Promise<BulkOperation> {
    await this.validateRepositorySafety({
      operation: 'ROLLBACK_BULK_OPERATION',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'BulkOperation',
      criticalityLevel: 'CRITICAL',
      metadata: {
        batchId: input.batchId,
        reason: input.reason
      }
    });

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation: ROLLBACK_BULK_OPERATION_MUTATION,
        variables: { input },
        context: {
          headers: { 'x-tenant': input.tenantId }
        }
      });

      const operation = result.data?.rollbackBulkOperation;

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'ROLLBACK_BULK_OPERATION',
          tenantId: input.tenantId,
          entityType: 'BulkOperation',
          entityId: input.batchId,
          metadata: {
            reason: input.reason,
            successCount: operation.successCount,
            failureCount: operation.failureCount
          },
          timestamp: Date.now()
        });
      }

      return operation;
    } catch (error) {
      console.error('[BulkOperationRepository] Failed to rollback bulk operation:', error);
      throw error;
    }
  }
}

export default BulkOperationRepository;
